import {
  ButtonName,
  getPurchaseRewardAmountForProductV2,
  ProductRewardSearchResult,
} from "@brandclub/common-ui";
import { scaleFont } from "@/utils/font";
import {
  ClickAwayListener,
  Popper,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, useEffect } from "react";
import { formatMoney } from "../../../../../utils/StringUtils";
import {
  OutlineButton,
  SolidButton,
} from "../../../../StoreComponents/StoreButton";
import BagButton from "./BagButton";
import HamburgerMenuButton from "./HamburgerMenuButton";
import { useNavigate } from "react-router-dom";
import {
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "../../../../AppNavigation/constants";
import { motion } from "motion/react";
import { useAppSelector } from "@/redux/hooks";
import { getCartCounts } from "@/redux/selectors";

const Container = styled(motion.div)(
  ({ inPageCheckout }: { inPageCheckout: boolean }) => ({
    display: inPageCheckout ? "none" : "flex",

    flexDirection: "column",
    position: "fixed",
    bottom: 0,
    zIndex: 10,
    width: "100%",
    backgroundColor: "white",
    boxShadow: "0 -0px 10px 0px rgba(195, 188, 188, 0.15)",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: "flex",
    },
  })
);

const TopBar = styled("div")(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  gap: "10px",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "5px 24px",
  minHeight: "74px",
  height: "86px",
  borderTop: "1px solid rgba(220, 221, 220, 0.5)",

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: "5px 11px",
  },
}));

const HamburgerMenu = styled("div")({
  display: "none",
  alignItems: "center",
  padding: "11px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: "flex",
  },
});

const Dropdown = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: "12px 8px",
  boxSizing: "border-box",
  border: "1px solid #dcdddc80",
  backgroundColor: " #ffffff",
  shadow: "0 3px 15px 0 rgba(195, 188, 188, 0.15)",
  borderRadius: "8px",
  minWidth: "180px",
});

const RewardsInfo = styled("div")(({ theme }) => ({
  display: "none",
  justifyContent: "center",
  alignItems: "center",
  color: "var(--color-primary)",
  paddingBottom: "10px",
  fontWeight: 500,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: "flex",
    baseFontSize: scaleFont(theme.typography.fontSize, 12),
  },
}));

const RewardsInfoDesktop = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "var(--color-primary)",
  fontSize: scaleFont(theme.typography.fontSize, 16),
  cursor: "pointer",

  fontWeight: 500,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: "none",
  },
}));

const FlexEndContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  justifyContent: "flex-end",
  gap: "10px",
  alignItems: "center",
}));

const BagButtonContainer = styled("div")(() => ({
  display: "flex",
  gap: "10px",
  alignItems: "center",
}));

const BarOutlineButton = styled(OutlineButton)(({ theme }) => ({
  fontSize: scaleFont(theme.typography.fontSize, 16),
  fontWeight: theme.settings?.fonts?.fontWeight,
  borderWidth: 1,
  borderColor: theme.vars.palette.divider,
  width: "fit-content",
  height: 52,
  padding: "0px 20px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    height: 46,
    fontSize: scaleFont(theme.typography.fontSize, 14),
  },
}));

const getAllProductsReward = (products: ProductRewardSearchResult[]) => {
  const rewards = products
    .map((product) => getPurchaseRewardAmountForProductV2(product).amount)
    .filter((reward) => reward > 0);
  const totalRewards = rewards.reduce((acc, reward) => acc + reward, 0);
  const rewardCount = rewards.length;
  return [totalRewards, rewardCount];
};

const formatProductReward = (rewardAmount: number, numberOfRewards: number) => {
  if (numberOfRewards > 1) {
    return `${formatMoney(rewardAmount)} in rewards`;
  }
  return `${formatMoney(rewardAmount)} reward`;
};

const MenuContainer = styled("div")(() => ({
  display: "flex",
  gap: "10px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: "none",
  },
}));

const menuItems = [
  {
    label: "Profile",
    to: "/profile",
  },
  {
    label: "Rewards",
    to: "/rewards/balance",
  },
  {
    label: "Accounts",
    to: "/accounts",
  },
  {
    label: "Purchases",
    to: "/purchase",
  },
  {
    label: "Offers",
    to: "/offers",
  },
  {
    label: "Dashboard",
    to: "/me",
  },
];

const DashboardBottomBar = () => {
  const theme = useTheme();

  const inPageCheckout = theme.dtcMode === "inpage";
  const mobileView = useMediaQuery(`(max-width:${TABLET_SCREEN_SIZE}px)`);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const cartCount = useAppSelector(getCartCounts);
  const { products } = useAppSelector((state) => state?.checkout);
  const optimisedCart = useAppSelector(
    (state) => state.checkout.optimisticAllCarts
  );
  const allCarts = useAppSelector((state) => state.checkout.allCarts);

  const productsInOptimisedCart = optimisedCart
    ?.map((cart) => cart.items)
    .flat();
  const productsInAllCarts = allCarts?.map((cart) => cart.items).flat();

  const navigate = useNavigate();
  const handleToggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const productsArr = Object.values(products || {});
  const productsInCart = productsArr.filter((product) => {
    // check if product is in optimisedCart or allCarts

    if (productsInOptimisedCart && productsInOptimisedCart.length > 0) {
      return productsInOptimisedCart.some(
        (item) => item?.stacklineSku === product?.stacklineSku
      );
    } else if (productsInAllCarts && productsInAllCarts.length > 0) {
      return productsInAllCarts.some(
        (item) => item?.stacklineSku === product?.stacklineSku
      );
    }
    return false;
  });

  const [purchaseRewardAmount, rewardCount] =
    getAllProductsReward(productsInCart);
  const shouldShowBar = mobileView
    ? true
    : inPageCheckout
    ? false
    : cartCount > 0;

  const onCheckout = () => {
    navigate("checkout");
  };

  useEffect(() => {
    const footerElement = document.getElementById("dtc_footer");
    if (footerElement && shouldShowBar) {
      footerElement.style.paddingBottom = "86px";
    } else if (footerElement && !shouldShowBar) {
      footerElement.style.paddingBottom = "0px";
    }

    return () => {
      if (footerElement) {
        footerElement.style.paddingBottom = "0px";
      }
    };
  }, [shouldShowBar]);

  const open = Boolean(anchorEl);
  const id = open ? "menu-popper" : undefined;

  if (!shouldShowBar) {
    return null;
  }

  return (
    <Container
      inPageCheckout={inPageCheckout}
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
      className="bottom_container"
    >
      <TopBar>
        <MenuContainer>
          <BarOutlineButton
            onClick={(e) => {
              navigate("/me");
            }}
          >
            Dashboard
          </BarOutlineButton>
        </MenuContainer>

        <HamburgerMenu
          onClick={handleToggleMenu}
          aria-describedby={id}
          role="button"
        >
          <HamburgerMenuButton />
        </HamburgerMenu>

        <Popper
          placement="top-start"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [10, 28],
              },
            },
          ]}
          className="menu-popper"
          sx={{
            zIndex: 1,
          }}
          id={id}
          open={open}
          anchorEl={anchorEl}
        >
          <ClickAwayListener onClickAway={handleToggleMenu as any}>
            <Dropdown>
              {menuItems.map((item) => {
                return (
                  <BarOutlineButton
                    key={item.label}
                    onClick={(e) => {
                      navigate(item.to);
                      handleToggleMenu(e);
                    }}
                  >
                    {item.label}
                  </BarOutlineButton>
                );
              })}
            </Dropdown>
          </ClickAwayListener>
        </Popper>

        <FlexEndContainer>
          {cartCount ? (
            <>
              {!!purchaseRewardAmount ? (
                <RewardsInfoDesktop
                  onClick={onCheckout}
                  sx={{
                    fontWeight: theme.settings?.fonts?.fontWeight,
                  }}
                >
                  {formatProductReward(purchaseRewardAmount, rewardCount)}
                </RewardsInfoDesktop>
              ) : null}
              <SolidButton
                buttonName={ButtonName.AddToBagButton}
                sx={(theme) => ({
                  fontSize: scaleFont(theme.typography.fontSize, 16),
                  fontWeight: theme.settings?.fonts?.fontWeight,
                  width: "fit-content",
                  height: 52,
                  padding: "0px 20px",
                  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                    fontSize: scaleFont(theme.typography.fontSize, 14),
                    display: "flex",
                    width: "100%",
                    height: 46,
                    maxWidth: "unset",
                  },
                  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
                    maxWidth: "unset",
                  },
                })}
                onClick={onCheckout}
              >
                Checkout
              </SolidButton>
            </>
          ) : null}
        </FlexEndContainer>
        <BagButtonContainer>
          <BagButton
            onClick={() => {
              navigate("/mybag");
            }}
          />
        </BagButtonContainer>
      </TopBar>
      {!!purchaseRewardAmount && (
        <RewardsInfo
          sx={{
            fontWeight: theme.settings?.fonts?.fontWeight,
          }}
        >
          {formatMoney(purchaseRewardAmount)} in rewards
        </RewardsInfo>
      )}
    </Container>
  );
};

export default DashboardBottomBar;
